import React, { useMemo } from 'react';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import {
  Plus,
  Minus,
  Loader2,
  Info,
  Settings,
  ShieldCheck,
  Box,
  FileText,
  Upload,
  Trash2,
  ChevronDown,
  X,
  Grid3x3,
  ArrowLeft
} from 'lucide-react';
import type { AddModalState } from './project-asset-tree-view/types';
import type { VisualEquipment } from './project-asset-tree-view/types';
import type { ProjectAsset } from '../../../services/projectAssetService';

export interface BulkEquipmentDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  addModalState: AddModalState;
  setAddModalState: React.Dispatch<React.SetStateAction<AddModalState>>;
  isBulkCreatingEquipment: boolean;
  onBulkCreate: () => void;
  manufacturers: Array<{ _id?: string; id?: string; name: string }>;
  dropdownMakeAndModels: Array<{ id?: string; _id?: string; name: string }>;
  newBulkDocument: { documentName: string; documentType: string; documentFile: string };
  setNewBulkDocument: React.Dispatch<React.SetStateAction<{ documentName: string; documentType: string; documentFile: string }>>;
  onAddBulkDocument: () => void;
  onRemoveBulkDocument: (index: number) => void;
  onOpenManufacturerCreator: () => void;
  onOpenMakeAndModelCreator: () => void;
  equipmentList: Array<{ _id?: string; id?: string; parentId?: string; assetType?: string }>;
  /** When provided, drawer shows equipment type picker first (with count + suggestions) */
  projectAssets?: ProjectAsset[];
  visualEquipmentList?: VisualEquipment[];
  availableAssets?: unknown[];
  getAssetTypeConfig?: (assetType: string, availableAssets: unknown[]) => { icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>; color: string };
}

export default function BulkEquipmentDrawer({
  open,
  onClose,
  title,
  addModalState,
  setAddModalState,
  isBulkCreatingEquipment,
  onBulkCreate,
  manufacturers,
  dropdownMakeAndModels,
  newBulkDocument,
  setNewBulkDocument,
  onAddBulkDocument,
  onRemoveBulkDocument,
  onOpenManufacturerCreator,
  onOpenMakeAndModelCreator,
  equipmentList,
  projectAssets,
  availableAssets,
  getAssetTypeConfig
}: BulkEquipmentDrawerProps) {
  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title={addModalState.ghost ? `Add Bulk Equipment - ${addModalState.ghost.assetName}` : title}
      size="md"
      footer={
        <div className="flex justify-end gap-3 p-4">
          <button
            onClick={onClose}
            className="btn-premium px-4 py-2.5 text-[13px] font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500/30 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onBulkCreate}
            disabled={isBulkCreatingEquipment || !addModalState.ghost}
            className={`btn-premium px-5 py-2.5 text-[13px] font-semibold border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500/40 flex items-center gap-2 transition-all duration-200 ease-out active:scale-[0.98] ${
              isBulkCreatingEquipment || !addModalState.ghost
                ? 'bg-slate-400 cursor-not-allowed text-white shadow-none'
                : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md hover:shadow-lg'
            }`}
          >
            {isBulkCreatingEquipment && <Loader2 size={16} className="animate-spin" />}
            {isBulkCreatingEquipment ? 'Creating Equipment...' : 'Add Bulk Equipment'}
          </button>
        </div>
      }
    >
      <div className={`space-y-6 p-1 ${isBulkCreatingEquipment ? 'relative' : ''}`}>
        {isBulkCreatingEquipment && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={32} className="animate-spin text-[#339274]" />
              <p className="text-sm font-medium text-gray-700">Creating bulk equipment...</p>
              <p className="text-xs text-gray-500">Please wait while we create your equipment</p>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-3">
          <div className="bg-blue-100 p-1.5 rounded-full mt-0.5">
            <Info size={16} className="text-blue-600" />
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Bulk Creation</p>
            <p className="text-blue-700/80 text-xs">
              All equipment created in this batch will share the selected Manufacturer, Make & Model, and Warranty information.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Quantity</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() =>
                  setAddModalState(prev => {
                    const q = Math.max(1, prev.quantity - 1);
                    return {
                      ...prev,
                      quantity: q,
                      combinerBoxCount: q <= 28 ? undefined : prev.combinerBoxCount
                    };
                  })
                }
                disabled={isBulkCreatingEquipment}
                className={`btn-premium w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition-all duration-200 ${
                  isBulkCreatingEquipment ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-50 hover:border-slate-300 active:scale-95'
                }`}
              >
                <Minus size={18} />
              </button>
              <input
                type="number"
                min={1}
                max={100}
                value={addModalState.quantity}
                onChange={e => {
                  const q = Math.max(1, parseInt(e.target.value) || 1);
                  setAddModalState({
                    ...addModalState,
                    quantity: q,
                    combinerBoxCount: q <= 28 ? undefined : addModalState.combinerBoxCount
                  });
                }}
                disabled={isBulkCreatingEquipment}
                className={`flex-1 px-3 py-2.5 text-center font-bold text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#339274] outline-none ${
                  isBulkCreatingEquipment ? 'cursor-not-allowed opacity-50 bg-gray-50' : ''
                }`}
              />
              <button
                onClick={() =>
                  setAddModalState(prev => ({
                    ...prev,
                    quantity: prev.quantity + 1
                  }))
                }
                disabled={isBulkCreatingEquipment}
                className={`btn-premium w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition-all duration-200 ${
                  isBulkCreatingEquipment ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-50 hover:border-slate-300 active:scale-95'
                }`}
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {(() => {
            const isSolarModule = addModalState.ghost?.assetName &&
              (addModalState.ghost.assetName.toLowerCase().includes('module') ||
                addModalState.ghost.assetName.toLowerCase().includes('solar') ||
                addModalState.ghost.assetName.toLowerCase().includes('panel'));
            const parentEqForString = addModalState.ghost?.parentId
              ? equipmentList.find(e => (e._id || e.id) === addModalState.ghost?.parentId)
              : null;
            const parentTypeStr = parentEqForString?.assetType || addModalState.ghost?.parentType;
            const isInvParent = parentTypeStr && parentTypeStr.toLowerCase().includes('inverter');
            const showStringInput = isSolarModule && isInvParent && addModalState.quantity >= 10;
            const showCombinerInput = showStringInput && addModalState.quantity > 28;
            if (!showStringInput) return null;
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Grid3x3 size={16} className="text-blue-600" />
                    <label className="block text-sm font-bold text-blue-900">Group into Strings</label>
                  </div>
                  <p className="text-xs text-blue-700 mb-3">
                    Organize {addModalState.quantity} modules into strings for better management
                  </p>
                  <div>
                    <label className="block text-xs font-medium text-blue-900 mb-1">Number of Strings</label>
                    <input
                      type="number"
                      min={1}
                      max={addModalState.quantity}
                      value={addModalState.stringCount ?? ''}
                      onChange={e => {
                        const count = parseInt(e.target.value) || 0;
                        setAddModalState(prev => {
                          let combinerBoxCount = prev.combinerBoxCount;
                          if (
                            combinerBoxCount != null &&
                            combinerBoxCount > 0 &&
                            count > 0 &&
                            combinerBoxCount > count
                          ) {
                            combinerBoxCount = count;
                          }
                          return { ...prev, stringCount: count, combinerBoxCount };
                        });
                      }}
                      placeholder="e.g., 4"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    />
                    {addModalState.stringCount != null && addModalState.stringCount > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        Each string will have ~{Math.ceil(addModalState.quantity / addModalState.stringCount)} modules
                      </p>
                    )}
                  </div>
                </div>

                {showCombinerInput && (
                  <div className="border-t border-blue-200/80 pt-3 rounded-md bg-white/40 px-1 -mx-1">
                    <p className="text-xs font-semibold text-blue-900 mb-1">
                      String combiner boxes{' '}
                      <span className="font-normal text-blue-600/90">(optional)</span>
                    </p>
                    <p className="text-[11px] text-blue-700/90 mb-2">
                      Optional extra level: inverter → combiner box(es) → strings → modules. Leave this blank to keep
                      the default layout (strings directly under the inverter). If you enter a count, strings are split
                      evenly across combiner boxes (at most one combiner per string).
                    </p>
                    <label className="block text-xs font-medium text-blue-900 mb-1">
                      Number of string combiner boxes
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={addModalState.stringCount && addModalState.stringCount > 0 ? addModalState.stringCount : addModalState.quantity}
                      value={addModalState.combinerBoxCount ?? ''}
                      onChange={e => {
                        const raw = e.target.value.trim();
                        if (raw === '') {
                          setAddModalState({ ...addModalState, combinerBoxCount: undefined });
                          return;
                        }
                        const n = parseInt(raw, 10);
                        if (Number.isNaN(n) || n < 1) {
                          setAddModalState({ ...addModalState, combinerBoxCount: undefined });
                          return;
                        }
                        setAddModalState({ ...addModalState, combinerBoxCount: n });
                      }}
                      placeholder="Optional — e.g. 4, or leave blank"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      aria-label="Number of string combiner boxes (optional)"
                    />
                    {addModalState.combinerBoxCount != null &&
                      addModalState.combinerBoxCount > 0 &&
                      addModalState.stringCount != null &&
                      addModalState.stringCount > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          ~{Math.ceil(addModalState.stringCount / addModalState.combinerBoxCount)} strings per
                          combiner box (distributed evenly)
                        </p>
                      )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="border-t border-gray-100 pt-4 space-y-4">
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Settings size={16} className="text-gray-500" />
              Optional Configuration
            </h4>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Manufacturer</label>
              <div className="flex gap-2">
                <select
                  value={addModalState.manufacturer ?? ''}
                  onChange={e => setAddModalState({ ...addModalState, manufacturer: e.target.value, makeAndModel: '', makeAndModelId: '' })}
                  disabled={isBulkCreatingEquipment}
                  className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#339274] focus:border-transparent outline-none transition-all ${
                    isBulkCreatingEquipment ? 'cursor-not-allowed opacity-50 bg-gray-50' : ''
                  }`}
                >
                  <option value="">Select Manufacturer...</option>
                  {manufacturers.map(mfg => (
                    <option key={mfg._id ?? mfg.id} value={mfg._id ?? mfg.id}>
                      {mfg.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={onOpenManufacturerCreator}
                  disabled={isBulkCreatingEquipment}
                  className={`btn-premium px-3 py-2 text-white rounded-xl transition-all duration-200 flex items-center justify-center ${
                    isBulkCreatingEquipment
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-sm hover:shadow'
                  }`}
                  title="Add New Manufacturer"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Make & Model</label>
              <div className="flex gap-2">
                <select
                  value={addModalState.makeAndModelId ?? ''}
                  onChange={e => {
                    const selectedModel = dropdownMakeAndModels.find(m => (m.id ?? m._id) === e.target.value);
                    setAddModalState({
                      ...addModalState,
                      makeAndModelId: e.target.value,
                      makeAndModel: selectedModel?.name ?? ''
                    });
                  }}
                  disabled={!addModalState.manufacturer || isBulkCreatingEquipment}
                  className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#339274] focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400 ${
                    isBulkCreatingEquipment ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                >
                  <option value="">Select Make & Model...</option>
                  {dropdownMakeAndModels.length > 0 ? (
                    dropdownMakeAndModels.map(mm => (
                      <option key={mm.id ?? mm._id} value={mm.id ?? mm._id}>
                        {mm.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      {addModalState.manufacturer ? 'No models found' : 'Select Manufacturer first'}
                    </option>
                  )}
                </select>
                <button
                  type="button"
                  onClick={onOpenMakeAndModelCreator}
                  disabled={!addModalState.manufacturer || isBulkCreatingEquipment}
                  className={`btn-premium px-3 py-2 text-white rounded-xl transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                    isBulkCreatingEquipment || !addModalState.manufacturer
                      ? 'bg-slate-400'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-sm hover:shadow'
                  }`}
                  title="Add New Make & Model"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <h5 className="text-xs font-bold text-gray-900 mb-3 flex items-center gap-2">
                <ShieldCheck size={14} className="text-blue-600" />
                Warranty Information
              </h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Provider</label>
                  <input
                    type="text"
                    value={addModalState.warranty?.provider ?? ''}
                    onChange={e =>
                      setAddModalState({
                        ...addModalState,
                        warranty: { ...addModalState.warranty!, provider: e.target.value }
                      })
                    }
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Provider Name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Duration (Years)</label>
                  <input
                    type="number"
                    min={0}
                    value={addModalState.warranty?.termsYears ?? ''}
                    onChange={e =>
                      setAddModalState({
                        ...addModalState,
                        warranty: { ...addModalState.warranty!, termsYears: parseFloat(e.target.value) || 0 }
                      })
                    }
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="0"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Terms / Description</label>
                  <textarea
                    value={addModalState.warranty?.terms ?? ''}
                    onChange={e =>
                      setAddModalState({
                        ...addModalState,
                        warranty: { ...addModalState.warranty!, terms: e.target.value }
                      })
                    }
                    rows={2}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                    placeholder="Warranty terms details..."
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <h5 className="text-xs font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Box size={14} className="text-indigo-600" />
                Other Details
              </h5>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Vendor Name</label>
                  <input
                    type="text"
                    value={addModalState.vendor?.name ?? ''}
                    onChange={e =>
                      setAddModalState({
                        ...addModalState,
                        vendor: { ...addModalState.vendor!, name: e.target.value }
                      })
                    }
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="Vendor Name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">General Notes</label>
                  <textarea
                    value={addModalState.notes ?? ''}
                    onChange={e => setAddModalState({ ...addModalState, notes: e.target.value })}
                    rows={2}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                    placeholder="Add notes..."
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <h5 className="text-xs font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText size={14} className="text-indigo-600" />
                Documents
              </h5>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-4 transition-all hover:border-indigo-300">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 ml-1">Document Details</label>
                    <input
                      type="text"
                      value={newBulkDocument.documentName}
                      onChange={e => setNewBulkDocument(prev => ({ ...prev, documentName: e.target.value }))}
                      placeholder="e.g. User Manual, Warranty Card"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 ml-1">Type</label>
                      <div className="relative">
                        <select
                          value={newBulkDocument.documentType}
                          onChange={e => setNewBulkDocument(prev => ({ ...prev, documentType: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all bg-white"
                        >
                          {['Manual', 'Warranty', 'Certificate', 'Test Report', 'Other'].map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400">
                          <ChevronDown size={14} />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5 ml-1">File</label>
                      <input
                        type="file"
                        id="bulk-doc-upload"
                        className="hidden"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) setNewBulkDocument(prev => ({ ...prev, documentFile: file.name }));
                        }}
                      />
                      <label
                        htmlFor="bulk-doc-upload"
                        className={`flex items-center justify-center gap-2 w-full px-3 py-2 border rounded-lg text-sm cursor-pointer transition-all ${
                          newBulkDocument.documentFile
                            ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-medium'
                            : 'bg-white border-gray-300 text-gray-500 hover:border-indigo-300 hover:text-indigo-600'
                        }`}
                      >
                        {newBulkDocument.documentFile ? (
                          <React.Fragment>
                            <FileText size={16} />
                            <span className="truncate max-w-[100px]">{newBulkDocument.documentFile}</span>
                            <button
                              type="button"
                              onClick={e => {
                                e.preventDefault();
                                setNewBulkDocument(prev => ({ ...prev, documentFile: '' }));
                              }}
                              className="p-0.5 hover:bg-indigo-200 rounded ml-1"
                            >
                              <X size={12} />
                            </button>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <Upload size={16} />
                            <span>Select File</span>
                          </React.Fragment>
                        )}
                      </label>
                    </div>
                  </div>
                  <button
                    onClick={onAddBulkDocument}
                    disabled={!newBulkDocument.documentName || !newBulkDocument.documentFile}
                    className="btn-premium w-full py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white rounded-xl text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-[0.98] transition-all duration-200 ease-out"
                  >
                    <Plus size={16} />
                    Add Document
                  </button>
                </div>
              </div>
              {addModalState.documents && addModalState.documents.length > 0 && (
                <div className="space-y-2">
                  {addModalState.documents.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded hover:border-indigo-300 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        <div className="truncate">
                          <p className="font-medium text-xs text-gray-900 truncate">{doc.documentName}</p>
                          <p className="text-[10px] text-gray-500">{doc.documentType}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveBulkDocument(index)}
                        className="btn-premium p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </RightDrawer>
  );
}
